import {Component, effect, inject, OnDestroy, OnInit, Signal} from '@angular/core';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {_DEFAULT_USER_LOGGING, IStateLogger, IUser, IUserLogger} from '../../../user-access/models';
import {Store} from '@ngrx/store';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {selectUserAccessUserProfile} from '../../../user-access/user-access-store/user-access.selectors';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {ICrudOptions, IUserAccessUserProfile} from 'src/app/features/user-access/models/user-access-features/access';
import {getFieldErrorString, PHONE_PATTERN_REGEX} from '../../../../shared/utils/form/form.utils';
import {compressImageToBase64} from '../../../../shared/utils/image/image.utils';
import {
  changeUserPassword,
  resetUserPassword,
  updateUserProfileDetails
} from '../../../user-access/user-store/user.actions';
import {selectSelectedSetting} from '../../store/setting.selectors';
import {SettingsReferenceEnum} from '../../models/general/settings-reference.enum';
import {settingsSetSelected} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';

@Component({
  selector: 'app-user-profile-settings',
  templateUrl: './user-profile-settings.component.html',
  styleUrl: './user-profile-settings.component.scss',
  standalone: false,
})
export class UserProfileSettingsComponent implements OnInit, OnDestroy, IHasUnsavedChanges {

  //#region INJECTORS
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly store: Store = inject(Store);
  private readonly modalUtils: SharedModalUtils = inject(SharedModalUtils);
  private readonly unsavedChangesService: UnsavedChangesService = inject(UnsavedChangesService);
  //#endregion

  //#region PROPERTIES
  formContactDetails: FormGroup;
  formPersonalDetails: FormGroup;
  formProfileDetails: FormGroup;
  formChangePassword: FormGroup;

  isHoveringProfileImage: boolean = false;
  isChangePasswordFormVisible: boolean = false;
  technicalSupport: IStateLogger & IUserLogger = {..._DEFAULT_USER_LOGGING};

  protected readonly icons: typeof IconsEnum = IconsEnum;
  protected readonly iconColours: typeof IconColourEnum = IconColourEnum;
  protected readonly tooltips: typeof _DEFAULT_APPLICATION_TOOLTIPS = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly settingChanged: Signal<SettingsReferenceEnum> = this.store.selectSignal(selectSelectedSetting);
  private readonly user: Signal<IUser> = this.store.selectSignal(selectUser);
  private readonly uaUserProfile: Signal<IUserAccessUserProfile> = this.store.selectSignal(selectUserAccessUserProfile);
  #hasTechnicalSupportBeenEdited: boolean = false;
  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const currentUser: IUser = this.user();
      const access: IUserAccessUserProfile = this.uaUserProfile();

      this.initializeForms(access, currentUser);
    });
    effect(() => {
      const ref: SettingsReferenceEnum = this.settingChanged();
      this.store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }
  //#endregion

  //#region ACCESSOR - USER CONTACT DETAILS

  get getInitials(): string {
    const { firstname = '', lastname = '' } =
    this.user()?.personalDetails ?? {};

    return `${firstname[0] ?? ''}${lastname[0] ?? ''}`.toUpperCase();
  }

  get hasUserContactDetailsBeenEdited(): boolean {
    return this.formContactDetails?.dirty ?? false;
  }

  get isUserContactDetailsSubmitDisabled(): boolean {
    return (this.canUserContactDetailsBeEdited === false) || (this.canUserContactDetailsBeEdited === true && this.hasUserContactDetailsBeenEdited === false) || (this.formContactDetails?.invalid ?? true);
  }

  get canUserContactDetailsBeEdited(): boolean {
    const access = this.userSettingsUserAccess?.userContactDetails;
    return access?.canEdit ?? false;
  }

  get canUserContactDetailsBeSubmitted(): boolean {
    const access = this.userSettingsUserAccess?.userContactDetails;
    return access?.canSubmit ?? false;
  }

  get canUserContactDetailsBeViewed(): boolean {
    const access = this.userSettingsUserAccess?.userContactDetails;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER PERSONAL DETAILS

  get hasUserPersonalDetailsBeenEdited(): boolean {
    return this.formPersonalDetails?.dirty ?? false;
  }

  get hasUserContactDetailsErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const cellphoneErrors = this.formContactDetails.controls['cellphone']?.errors;
    const emailErrors = this.formContactDetails.controls['email']?.errors;
    if (emailErrors) {
      errors.push({
        errors: emailErrors, error: getFieldErrorString(emailErrors, 'email'), field: 'email',
      });
    }
    if (cellphoneErrors) {
      errors.push({
        errors: cellphoneErrors, error: getFieldErrorString(cellphoneErrors, 'cellphone'), field: 'cellphone',
      });
    }
    return errors;
  }

  get isUserPersonalDetailsSubmitDisabled(): boolean {
    return (this.canUserPersonalDetailsBeEdited === false) || (this.canUserPersonalDetailsBeEdited === true && this.hasUserPersonalDetailsBeenEdited === false) || (this.formPersonalDetails?.invalid ?? true);
  }

  get canUserPersonalDetailsBeEdited(): boolean {
    const access = this.userSettingsUserAccess?.userPersonalDetails;
    return access?.canEdit ?? false;
  }

  get canUserPersonalDetailsBeSubmitted(): boolean {
    const access = this.userSettingsUserAccess?.userPersonalDetails;
    return access?.canSubmit ?? false;
  }

  get canUserPersonalDetailsBeViewed(): boolean {
    const access = this.userSettingsUserAccess?.userPersonalDetails;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER PROFILE DETAILS

  get hasUserProfileDetailsBeenEdited(): boolean {
    return this.formProfileDetails?.dirty ?? false;
  }

  get hasUserProfileDetailsErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const usernameErrors = this.formProfileDetails.controls['username']?.errors;
    if (usernameErrors) {
      errors.push({
        errors: usernameErrors, error: getFieldErrorString(usernameErrors, 'username'), field: 'username',
      });
    }
    return errors;
  }

  get isUserProfileDetailsSubmitDisabled(): boolean {
    return (this.canUserProfileDetailsBeEdited === false) || (this.canUserProfileDetailsBeEdited === true && this.hasUserProfileDetailsBeenEdited === false) || (this.formProfileDetails?.invalid ?? true);
  }

  get canUserProfileDetailsBeEdited(): boolean {
    const access = this.userSettingsUserAccess?.userProfileDetails;
    return access?.canEdit ?? false;
  }

  get canUserProfileDetailsBeSubmitted(): boolean {
    const access = this.userSettingsUserAccess?.userProfileDetails;
    return access?.canSubmit ?? false;
  }

  get canUserProfileDetailsBeViewed(): boolean {
    const access = this.userSettingsUserAccess?.userProfileDetails;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - TECHNICAL SUPPORT

  get hasTechnicalSupportBeenEdited(): boolean {
    return this.#hasTechnicalSupportBeenEdited;
  }

  get isTechnicalSupportSubmitDisabled(): boolean {
    return (this.canTechnicalSupportBeEdited === false) || (this.canTechnicalSupportBeEdited === true && this.hasTechnicalSupportBeenEdited === false);
  }

  get canTechnicalSupportBeEdited(): boolean {
    const access = this.userSettingsUserAccess?.technicalSupport;
    return access?.canEdit ?? false;
  }

  get canTechnicalSupportBeSubmitted(): boolean {
    const access = this.userSettingsUserAccess?.technicalSupport;
    return access?.canSubmit ?? false;
  }

  get canTechnicalSupportBeViewed(): boolean {
    const access = this.userSettingsUserAccess?.technicalSupport;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - CHANGE PASSWORD

  get hasChangePasswordBeenEdited(): boolean {
    return this.formChangePassword?.dirty ?? false;
  }

  get hasChangePasswordErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const currentPasswordErrors = this.formChangePassword?.controls['currentPassword']?.errors;
    const newPasswordErrors = this.formChangePassword?.controls['newPassword']?.errors;
    const confirmNewPasswordErrors = this.formChangePassword?.controls['confirmNewPassword']?.errors;
    const formErrors = this.formChangePassword?.errors;

    if (currentPasswordErrors) {
      errors.push({
        errors: currentPasswordErrors, error: getFieldErrorString(currentPasswordErrors, 'current password'), field: 'currentPassword',
      });
    }
    if (newPasswordErrors) {
      errors.push({
        errors: newPasswordErrors, error: getFieldErrorString(newPasswordErrors, 'new password'), field: 'newPassword',
      });
    }
    if (confirmNewPasswordErrors) {
      errors.push({
        errors: confirmNewPasswordErrors, error: getFieldErrorString(confirmNewPasswordErrors, 'confirm password'), field: 'confirmNewPassword',
      });
    }
    if (formErrors?.['passwordMismatch']) {
      errors.push({
        errors: formErrors, error: 'Passwords do not match', field: 'passwordMismatch',
      });
    }
    return errors;
  }

  get isChangePasswordSubmitDisabled(): boolean {
    return (this.canChangePasswordBeEdited === false) || (this.canChangePasswordBeEdited === true && this.hasChangePasswordBeenEdited === false) || (this.formChangePassword?.invalid ?? true);
  }

  get canChangePasswordBeEdited(): boolean {
    const access = this.userSettingsUserAccess?.userProfileDetails;
    return access?.canEdit ?? false;
  }

  get canChangePasswordBeSubmitted(): boolean {
    const access = this.userSettingsUserAccess?.userProfileDetails;
    return access?.canSubmit ?? false;
  }

  get canChangePasswordBeViewed(): boolean {
    const access = this.userSettingsUserAccess?.userProfileDetails;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER SETTINGS + USER ACCESS
  get userSettings(): IUser {
    return this.user();
  }

  get userSettingsUserAccess(): IUserAccessUserProfile {
    return this.uaUserProfile();
  }

  //#endregion

  //#region ACCESSOR - ALL/GLOBAL
  get haveAnyEditsBeenMade(): boolean {
    return (this.formContactDetails?.dirty ?? false) || (this.formPersonalDetails?.dirty ?? false) ||
      (this.formProfileDetails?.dirty ?? false) || (this.formChangePassword?.dirty ?? false) || (this.#hasTechnicalSupportBeenEdited);
  }

  //#endregion

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.formContactDetails = this.formBuilder.group({});
    this.formPersonalDetails = this.formBuilder.group({});
    this.formProfileDetails = this.formBuilder.group({});
    this.formChangePassword = this.formBuilder.group({});
    this.unsavedChangesService.registerComponent(this);
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onChangeProfilePictureClick(): void {
    const fileInput: HTMLElement | null = document.getElementById('changeProfileImage');
    if (fileInput) {
      fileInput.click();
    }
  }

  async onProfilePictureChange(event: Event): Promise<void> {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (input?.files?.length > 0) {
      const file: File = input.files[0];
      const compressedBase64: string | null = await compressImageToBase64(file, 120, 120, 0.5);
      if (compressedBase64) {
        this.formProfileDetails.controls['pp'].setValue(compressedBase64);
        this.formProfileDetails.markAsDirty();
      }
    }
  }

  onDefaultValue(): void {
    if (!this.canTechnicalSupportBeSubmitted) {
      return;
    }
    this.technicalSupport = {..._DEFAULT_USER_LOGGING};
    this.#hasTechnicalSupportBeenEdited = (
      this.technicalSupport.logActions !== false &&
      this.technicalSupport.logState !== false &&
      this.technicalSupport.logErrors !== false &&
      this.technicalSupport.logInfo !== false &&
      this.technicalSupport.logSuccess !== false
    );
    this.onSubmitForm('technicalSupport');
  }

  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertContactDetails();
        this.revertPersonalDetails();
        this.revertProfileDetails();
        this.revertTechnicalSupport();
        this.revertChangePassword();
        break;
      case 'contactDetails':
        this.revertContactDetails();
        break;
      case 'personalDetails':
        this.revertPersonalDetails();
        break;
      case 'profileDetails':
        this.revertProfileDetails();
        break;
      case 'technicalSupport':
        this.revertTechnicalSupport();
        break;
      case 'changePassword':
        this.revertChangePassword();
        break;
    }
  }

  onSubmitForm(form: string): void {
    let data: Partial<IUser> = { id: this.userSettings.id };
    let hasChanges = false;

    switch (form) {

      case 'all': {
        const formsToSave = [
          {
            dirty: this.formContactDetails.dirty,
            patch: (): Partial<IUser> => this.generateContactDetailsPatch(data),
            reset: (): void => {
              this.formContactDetails.reset()
            }
          },
          {
            dirty: this.formPersonalDetails.dirty,
            patch: (): Partial<IUser> => this.generatePersonalDetailsPatch(data),
            reset: (): void => {
              this.formPersonalDetails.reset()
            }
          },
          {
            dirty: this.formProfileDetails.dirty,
            patch: (): Partial<IUser> => this.generateUserProfilePatch(data),
            reset: (): void => {
              this.formProfileDetails.reset()
            }
          },
          {
            dirty: this.#hasTechnicalSupportBeenEdited,
            patch: (): Partial<IUser> => this.generateTechnicalSupportPatch(data),
            reset: (): void => {
              this.#hasTechnicalSupportBeenEdited = false;
              }
          },
        ];

        formsToSave.forEach(form => {
          if (form.dirty) {
            data = { ...data, ...form.patch() };
            form.reset();
            hasChanges = true;
          }
        });

        break;
      }

      case 'technicalSupport':
        if (this.canTechnicalSupportBeSubmitted && this.#hasTechnicalSupportBeenEdited) {
          data = { ...data, ...this.generateTechnicalSupportPatch(data) };
          this.#hasTechnicalSupportBeenEdited = false;
          hasChanges = true;
        }
        break;

      default:
        break;
    }

    if (!hasChanges) return;

    this.store.dispatch(updateUserProfileDetails.updateUserProfile({ data }));
  }


  onToggleSwitch(property: string, toggleValue: boolean): void {
    if (this.technicalSupport[property] !== toggleValue) {
      this.technicalSupport[property] = toggleValue;
      this.#hasTechnicalSupportBeenEdited = true;
    }
  }

  onResetPassword(): void {
    const userEmail: string = this.userSettings?.contactDetails?.email ?? this.userSettings?.id;
    if (userEmail) {
      this.store.dispatch(resetUserPassword({userEmail}));
    }
  }

  onShowChangePasswordForm(): void {
    this.isChangePasswordFormVisible = true;
  }

  onCancelChangePassword(): void {
    this.formChangePassword.reset({
      'currentPassword': '',
      'newPassword': '',
      'confirmNewPassword': '',
    });
    this.isChangePasswordFormVisible = false;
  }

  onChangePassword(): void {
    if (!this.canChangePasswordBeSubmitted || this.formChangePassword.invalid) {
      return;
    }

    const currentPassword: string = this.formChangePassword.controls['currentPassword']?.value?.trim();
    const newPassword: string = this.formChangePassword.controls['newPassword']?.value?.trim();

    if (currentPassword && newPassword) {
      this.store.dispatch(changeUserPassword.changePassword({currentPassword, newPassword}));
      this.formChangePassword.reset();
      this.isChangePasswordFormVisible = false;
    }
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessUserProfile, user: IUser): void {
    this.buildContactDetailsForm(access?.userContactDetails, user);
    this.buildTechnicalSupportForm(access?.technicalSupport, user);
    this.buildPersonalDetailsForm(access?.userPersonalDetails, user);
    this.buildProfileDetailsForm(access?.userProfileDetails, user);
    this.buildChangePasswordForm(access?.userProfileDetails);
  }

  private buildContactDetailsForm(access: ICrudOptions, user: IUser): void {
    if (access?.canView && this.formContactDetails.pristine) {
      const tempForm: FormGroup = this.formBuilder.group({});
      this.addFormControl(
        tempForm,
        'cellphone',
        user?.contactDetails?.cellphone ?? undefined,
        false,
        [Validators.pattern(PHONE_PATTERN_REGEX)]
      );
      this.addFormControl(
        tempForm,
        'email',
        user?.contactDetails?.email ?? user.id ?? undefined,
        true,
        [Validators.email]
      );
      tempForm.markAsPristine();
      this.formContactDetails = tempForm;
    }
  }

  private buildTechnicalSupportForm(access: ICrudOptions, user: IUser): void {
    if (access?.canView && this.hasTechnicalSupportBeenEdited === false) {
      this.technicalSupport = {
        ...user.logUser,
        ...user.logState,
      };
    }
  }

  private buildPersonalDetailsForm(access: ICrudOptions, user: IUser): void {
    if (access?.canView && this.formPersonalDetails.pristine) {
      const tempForm: FormGroup = this.formBuilder.group({});
      this.addFormControl(tempForm, 'firstname', user?.personalDetails?.firstname ?? undefined, false, []);
      this.addFormControl(tempForm, 'lastname', user?.personalDetails?.lastname ?? undefined, false, []);
      tempForm.markAsPristine();
      this.formPersonalDetails = tempForm;
    }
  }

  private buildProfileDetailsForm(access: ICrudOptions, user: IUser): void {
    if (access?.canView && this.formProfileDetails.pristine) {
      const tempForm: FormGroup = this.formBuilder.group({});
      this.addFormControl(tempForm, 'pp', user.pp ?? undefined, access.canEdit === false, []);
      this.addFormControl(
        tempForm,
        'username',
        user.userName ?? undefined,
        access.canEdit === false,
        [Validators.required]
      );
      tempForm.markAsPristine();
      this.formProfileDetails = tempForm;
    }
  }

  private buildChangePasswordForm(access: ICrudOptions): void {
    if (access?.canView && this.formChangePassword.pristine) {
      const tempForm: FormGroup = this.formBuilder.group({
        currentPassword: ['', [Validators.required, Validators.minLength(8)]],
        newPassword: ['', [
          Validators.required,
          Validators.minLength(8),
          this.passwordComplexityValidator()
        ]],
        confirmNewPassword: ['', [Validators.required, Validators.minLength(8)]],
      }, {validators: this.passwordMatchValidator()});
      tempForm.markAsPristine();
      this.formChangePassword = tempForm;
    }
  }

  private passwordComplexityValidator(): ValidatorFn {
    return (control): ValidationErrors | null => {
      const value: string = control.value;

      if (!value) {
        return null;
      }

      const hasUppercase: boolean = /[A-Z]/.test(value);
      const hasLowercase: boolean = /[a-z]/.test(value);
      const hasDigit: boolean = /\d/.test(value);
      const hasSpecialChar: boolean = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

      const errors: ValidationErrors = {};

      if (!hasUppercase) {
        errors['requiresUppercase'] = true;
      }
      if (!hasLowercase) {
        errors['requiresLowercase'] = true;
      }
      if (!hasDigit) {
        errors['requiresDigit'] = true;
      }
      if (!hasSpecialChar) {
        errors['requiresSpecialChar'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  private passwordMatchValidator(): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const newPassword = formGroup.get('newPassword');
      const confirmNewPassword = formGroup.get('confirmNewPassword');

      if (!newPassword || !confirmNewPassword) {
        return null;
      }

      if (confirmNewPassword.errors && !confirmNewPassword.errors['passwordMismatch']) {
        return null;
      }

      if (newPassword.value !== confirmNewPassword.value) {
        confirmNewPassword.setErrors({passwordMismatch: true});
        return {passwordMismatch: true};
      } else {
        confirmNewPassword.setErrors(null);
        return null;
      }
    };
  }

  private addFormControl<T = unknown>(
    form: FormGroup,
    controlName: string,
    value: T,
    disabled: boolean,
    validators: ValidatorFn[] = []
  ): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  private revertContactDetails(): void {
    this.formContactDetails.reset({
      'cellphone': this.userSettings.contactDetails?.cellphone ?? undefined,
      'email': this.userSettings.contactDetails?.email ?? this.userSettings?.id ?? undefined,
    });
  }

  private revertPersonalDetails(): void {
    this.formPersonalDetails.reset({
      'firstname': this.userSettings.personalDetails?.firstname ?? undefined,
      'lastname': this.userSettings.personalDetails?.lastname ?? undefined,
    });
  }

  revertProfileDetails(): void {
    this.formProfileDetails.reset({
      'pp': this.userSettings.pp, 'username': this.userSettings.userName,
    });
  }

  revertTechnicalSupport(): void {
    this.technicalSupport = {
      ...this.userSettings?.logUser ?? {
        logErrors: false, logInfo: false, logSuccess: false,
      }, ...this.userSettings?.logState ?? {
        logActions: false, logState: false,
      },
    };
    this.#hasTechnicalSupportBeenEdited = false;
  }

  private revertChangePassword(): void {
    this.formChangePassword.reset({
      'currentPassword': '',
      'newPassword': '',
      'confirmNewPassword': '',
    });
    this.isChangePasswordFormVisible = false;
  }

  //#endregion

  //#region FORM - GENERATE PATCH DATA

  private generateContactDetailsPatch(data: Partial<IUser>): Partial<IUser> {
    const contactForm: unknown = this.formContactDetails.getRawValue();
    const currentCell: string | undefined = this.userSettings?.contactDetails?.cellphone?.trim();
    const currentEmail: string | undefined = this.userSettings?.contactDetails?.email?.toLowerCase()?.trim();
    const nextCell: string | undefined = contactForm?.['cellphone']?.trim();
    const nextEmail: string | undefined = contactForm?.['email']?.toLowerCase()?.trim();
    const contactPatch: Partial<IUser['contactDetails']> = {
      ...(currentCell !== nextCell && nextCell && {cellphone: nextCell}),
      ...(currentEmail !== nextEmail && nextEmail && {email: nextEmail}),
    };
    if (Object.keys(contactPatch).length === 0) {
      return data;
    }
    return {...data, contactDetails: contactPatch};
  }

  private generatePersonalDetailsPatch(data: Partial<IUser>): Partial<IUser> {
    const personal: unknown = this.formPersonalDetails.getRawValue();
    const currentFirstname: string | undefined = this.userSettings?.personalDetails?.firstname?.trim();
    const currentLastname: string | undefined = this.userSettings?.personalDetails?.lastname?.trim();
    const nextFirstname: string | undefined = personal?.['firstname']?.trim();
    const nextLastname: string | undefined = personal?.['lastname']?.trim();
    const personalPatch: Partial<IUser['personalDetails']> = {
      ...(currentFirstname !== nextFirstname && nextFirstname && {firstname: nextFirstname}),
      ...(currentLastname !== nextLastname && nextLastname && {lastname: nextLastname}),
    };
    if (Object.keys(personalPatch).length === 0) {
      return data;
    }
    return {...data, personalDetails: personalPatch};
  }

  private generateUserProfilePatch(data: Partial<IUser>): Partial<IUser> {
    const profileForm: unknown = this.formProfileDetails.getRawValue();
    const currentPp: string | undefined = this.userSettings?.pp;
    const currentUsername: string | undefined = this.userSettings?.userName?.toLowerCase()?.trim();
    const nextPp: string = profileForm?.['pp'] as string;
    const nextUsername: string | undefined = (profileForm?.['username'] as string)?.toLowerCase()?.trim();
    const profilePatch: Partial<IUser> = {
      ...(currentPp !== nextPp && nextPp && {pp: nextPp}),
      ...(currentUsername !== nextUsername && nextUsername && {userName: nextUsername}),
    };
    if (Object.keys(profilePatch).length === 0) {
      return data;
    }
    return {...data, ...profilePatch};
  }

  private generateTechnicalSupportPatch(data: Partial<IUser>): Partial<IUser> {
    const technicalPatch: Partial<IUser> = {
      logState: {
        logActions: this.technicalSupport?.logActions ?? false,
        logState: this.technicalSupport?.logState ?? false,
      } as IStateLogger,
      logUser: {
        logErrors: this.technicalSupport?.logErrors ?? false,
        logInfo: this.technicalSupport?.logInfo ?? false,
        logSuccess: this.technicalSupport?.logSuccess ?? false,
      } as IUserLogger,
    };
    return {
      ...data,
      logState: technicalPatch.logState,
      logUser: technicalPatch.logUser,
    };
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  /**
   * Shows a modal asking the user if they want to discard unsaved changes
   * @returns Promise that resolves to true if user wants to proceed (discard changes), false if user cancels
   */
  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  //#endregion
}
